import { ArchiveIcon, CheckmarkIcon, MoreIcon } from '@treams/treams-icons';
import { Link } from '@treams/treams-routes';
import {
	Paragraph,
	Separator,
	Avatar,
	Button,
	ContextMenu,
	Stat,
	Spinner,
	Ellipsis,
	Checkbox,
} from '@treams/treams-ui';
import * as graphql from 'generated/graphql';
import moment from 'moment';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Flex, Box } from 'reflexbox';
import styled from 'styled-components';
import useLocalize from '../packages/kakapo-ui-application/hooks/useLocalize';

const ConversationsTableRowElement = styled(Link)`
	display: block;

	:hover {
		background-color: ${(props) => props.theme.colors.whiteBlue};
	}
`;

function readableConversationNextStep(
	evaluationSubmission: graphql.EvaluationSubmission,
): 'prepare' | 'confirm' | 'complete' | 'archived' | 'none' {
	if (evaluationSubmission.manager.isCompleted) {
		return 'none';
	}

	if (evaluationSubmission.isArchived) {
		return 'archived';
	}

	if (evaluationSubmission.requiresSignature) {
		if (evaluationSubmission.manager.status === graphql.EvaluationSubmissionStatus.Signed) {
			return 'complete';
		}
		if (evaluationSubmission.manager.hasShared) {
			return 'confirm';
		}
	}

	if (evaluationSubmission.manager.hasShared) {
		return 'complete';
	}
	return 'prepare';
}

export function ConversationsTableRow(props: {
	evaluationSubmission: graphql.EvaluationSubmission;
	onArchive: (evaluationSubmission: graphql.EvaluationSubmission) => Promise<void>;
	onComplete: (evaluationSubmission: graphql.EvaluationSubmission) => Promise<void>;
	onUncomplete: (evaluationSubmission: graphql.EvaluationSubmission) => Promise<void>;
}) {
	const { localize } = useLocalize();
	const { t } = useTranslation();
	const [loading, setLoading] = useState(false);

	const completed = props.evaluationSubmission.manager.isCompleted;

	return (
		<Flex alignItems="center" ml={[0, 0, -32]}>
			<Box flexShrink={0} flexGrow={0} mr={2}>
				{loading ? (
					<Flex
						width={24}
						height={24}
						alignItems="center"
						justifyContent="center"
						flexGrow={0}
						flexShrink={0}
					>
						<Spinner />
					</Flex>
				) : (
					<Checkbox
						checked={completed}
						onClick={async () => {
							setLoading(true);
							if (completed) {
								await props.onUncomplete(props.evaluationSubmission);
							} else {
								await props.onComplete(props.evaluationSubmission);
							}
							setLoading(false);
						}}
					/>
				)}
			</Box>
			<Box flexGrow={1} flexShrink={1}>
				<Link
					to={`/users/${props.evaluationSubmission.employee.user.id}/conversations/${props.evaluationSubmission.id}?return=/manager`}
				>
					<ConversationsTableRowElement>
						<Flex alignItems="center" py={2} px={2}>
							<Box width={3 / 12} flexShrink={0}>
								<Flex alignItems="center">
									<Box flexShrink={0}>
										<Avatar user={props.evaluationSubmission.employee.user} size={24} mr={2} />
									</Box>
									<Box>
										<Paragraph color="antracite">
											{props.evaluationSubmission.employee.user.firstName}{' '}
											{props.evaluationSubmission.employee.user.lastName}
										</Paragraph>
										{props.evaluationSubmission.employee.user.jobPositions[0] && (
											<Paragraph>
												<Ellipsis>
													{
														localize(
															props.evaluationSubmission.employee.user.jobPositions[0].jobTitle
																.title,
														).text
													}
												</Ellipsis>
											</Paragraph>
										)}
									</Box>
								</Flex>
							</Box>
							<Box width={3 / 12} ml={3} flexShrink={0}>
								<Paragraph>
									<Ellipsis>
										{localize(props.evaluationSubmission.evaluationForm.title).text}
									</Ellipsis>
								</Paragraph>
							</Box>
							<Box width={3 / 24} ml={3} flexShrink={0}>
								<Paragraph
									color={
										moment(props.evaluationSubmission.deadline).isBefore(moment().startOf('day')) &&
										!completed
											? 'red'
											: 'slateBlue'
									}
								>
									{moment(props.evaluationSubmission.deadline).format('DD-MM-YYYY')}
								</Paragraph>
							</Box>
							<Flex flexGrow={1} justifyContent="space-between" alignItems="center" ml={3}>
								<Paragraph>
									{t(
										`conversation.table.row.progress.${readableConversationNextStep(
											props.evaluationSubmission,
										)}`,
									)}
								</Paragraph>
								<Flex flexGrow={0} flexShrink={0}>
									<Button
										ml={3}
										mr={2}
										small
										title={t('manager.conversations.forecast.start.button')}
										type="transparent"
									/>
									<ContextMenu.Target
										menu={
											<ContextMenu>
												{!props.evaluationSubmission.manager.isCompleted && (
													<ContextMenu.Item
														title={t('menu.actions.completeConversation')}
														icon={<CheckmarkIcon />}
														onClick={async () => {
															setLoading(true);
															await props.onComplete(props.evaluationSubmission);
															setLoading(false);
														}}
													/>
												)}
												<ContextMenu.Item
													title={t('menu.actions.archiveConversation')}
													icon={<ArchiveIcon />}
													onClick={async () => {
														setLoading(true);
														await props.onArchive(props.evaluationSubmission);
														setLoading(false);
													}}
												/>
											</ContextMenu>
										}
									>
										<Stat borderless icon={loading ? <Spinner /> : <MoreIcon />} tidy />
									</ContextMenu.Target>
								</Flex>
							</Flex>
						</Flex>
						<Separator />
					</ConversationsTableRowElement>
				</Link>
			</Box>
		</Flex>
	);
}

export function ConversationsTableMomentRow(props: {
	momentInstance: graphql.MomentInstance;
	onSkip: (momentInstance: graphql.MomentInstance) => Promise<void>;
}) {
	const { localize } = useLocalize();
	const { t } = useTranslation();

	return (
		<Link
			to={`/users/${props.momentInstance.user.id}/conversations/create-from-moment/${props.momentInstance.id}`}
		>
			<ConversationsTableRowElement>
				<Flex alignItems="center" py={2} px={2}>
					<Box width={3 / 12} flexShrink={0}>
						<Flex alignItems="center">
							<Box flexShrink={0}>
								<Avatar user={props.momentInstance.user} size={24} mr={2} />
							</Box>
							<Box>
								<Paragraph color="antracite">
									{props.momentInstance.user.firstName} {props.momentInstance.user.lastName}
								</Paragraph>
								{props.momentInstance.user.jobPositions[0] && (
									<Paragraph>
										<Ellipsis>
											{localize(props.momentInstance.user.jobPositions[0].jobTitle.title).text}
										</Ellipsis>
									</Paragraph>
								)}
							</Box>
						</Flex>
					</Box>
					<Box width={3 / 12} ml={3} flexShrink={0}>
						{props.momentInstance.momentTemplate.evaluation?.evaluationForm && (
							<Paragraph>
								<Ellipsis>
									{
										localize(props.momentInstance.momentTemplate.evaluation!.evaluationForm!.title)
											.text
									}
								</Ellipsis>
							</Paragraph>
						)}
					</Box>
					<Box width={3 / 24} ml={3} flexShrink={0}>
						<Paragraph
							color={
								moment(props.momentInstance.endDate).isBefore(moment().startOf('day'))
									? 'red'
									: 'slateBlue'
							}
						>
							{moment(props.momentInstance.endDate).format('DD-MM-YYYY')}
						</Paragraph>
					</Box>
					<Flex flexGrow={1} justifyContent="space-between" alignItems="center" ml={3}>
						<Paragraph>{t('conversation.table.row.progress.prepare')}</Paragraph>
						<Flex flexGrow={0} flexShrink={0} mr={4}>
							<Button
								ml={3}
								mr={2}
								small
								title={t('manager.conversations.forecast.create.button')}
								type="transparent"
							/>
						</Flex>
					</Flex>
				</Flex>
				<Separator />
			</ConversationsTableRowElement>
		</Link>
	);
}

const ConversationsTableHeaderCount = styled(Box)`
	width: 24px;
	height: 24px;
	background: ${(props) => props.theme.colors.red};
	color: ${(props) => props.theme.colors.white};
	border-radius: 24px;
	font-weight: 700;
	display: flex;
	align-items: center;
	justify-content: center;
`;

export function ConversationsTableHeader(props: {
	index: number;
	isDue?: boolean;
	isCompleted?: boolean;
	title: string;
	count: number;
}) {
	const { t } = useTranslation();
	return props.index === 0 ? (
		<Flex alignItems="center" ml={[0, 0, -32]}>
			<Box width={32} flexGrow={0} flexShrink={0} />
			<Box flexGrow={1} flexShrink={1}>
				<Flex alignItems="center" mt={4} mb={2}>
					<Box width={3 / 12}>
						<Paragraph color="antracite">
							<Flex alignItems="center">
								<strong>{props.title}</strong>
								{props.isDue && (
									<ConversationsTableHeaderCount ml={2}>
										{props.count}
									</ConversationsTableHeaderCount>
								)}
							</Flex>
						</Paragraph>
					</Box>
					<Box width={3 / 12} ml={3}>
						<Paragraph>
							<strong>{t('manager.conversations.forecast.table.heading.template')}</strong>
						</Paragraph>
					</Box>
					<Box width={3 / 24} ml={3}>
						<Paragraph>
							<strong>{t('manager.conversations.forecast.table.heading.deadline')}</strong>
						</Paragraph>
					</Box>
					<Box width={2 / 12} ml={3}>
						<Paragraph>
							<strong>{t('manager.conversations.forecast.table.heading.status')}</strong>
						</Paragraph>
					</Box>
				</Flex>
				<Separator />
			</Box>
		</Flex>
	) : (
		<Box>
			<Paragraph mt={4} mb={2} color="antracite">
				<Flex alignItems="center">
					<strong>{props.title}</strong>
					{props.isDue && (
						<ConversationsTableHeaderCount ml={2}>{props.count}</ConversationsTableHeaderCount>
					)}
				</Flex>
			</Paragraph>
			<Separator />
		</Box>
	);
}
